import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';
import {NodeProps} from 'reactflow';

import {
  getExpectedStepDataKind,
  getInputStepDataShape,
  getOutputStepDataShape,
} from 'pipelines/StepDataUtils';
import {getIconForStepDataKind} from 'pipelines/StepIconUtils';
import {BenchlingNodeV2} from 'ui-library/reactflow/nodes/BenchlingNode/v2/BenchlingNode';

import {AnalysisFlowchartAction} from './actions/AnalysisFlowchartActionTypes';
import {AnalysisGraphNodeData} from './analysis-graph/AnalysisGraphTypes';
import {useAnalysisFlowchartContext} from './AnalysisFlowchartContext';
import StepDataHandle from './handles/StepDataHandle';
import StepMenu from './menus/StepMenu';
import getStepNodeContents from './node-contents/getStepNodeContents';
import StepPopover from './popovers/StepPopover';

type AnalysisFlowchartNodeProps = Pick<
  NodeProps<AnalysisGraphNodeData>,
  'data' | 'dragging' | 'id' | 'xPos' | 'yPos'
>;

export default function AnalysisFlowchartNode({
  data: {step},
  dragging,
}: AnalysisFlowchartNodeProps): JSX.Element {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const {mode, getActionInfo} = useAnalysisFlowchartContext();

  const {nodeName, nodeIcon, nodeStatus, nodeSecondaryInfo} = getStepNodeContents(step, mode);

  return (
    <StepPopover step={step} disabled={isMenuOpen || dragging}>
      <StepMenu step={step} isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen}>
        <div
          className={css(styles.node)}
          onClick={() => {
            getActionInfo(AnalysisFlowchartAction.OPEN_STEP_CONFIGURATION, step.id).doAction?.(step.id);
          }}
          onContextMenu={(e) => {
            setIsMenuOpen(true);
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <BenchlingNodeV2.Standard.Node
            headerProps={{
              title: nodeName,
              leftIcon: nodeIcon,
              subTitle: nodeSecondaryInfo,
              leftHandle:
                step.inputStepDatas.length === 1 ? (
                  <StepDataHandle step={step} stepData={step.inputStepDatas[0]} />
                ) : null,
              rightHandle:
                step.outputStepDatas.length === 1 ? (
                  <StepDataHandle step={step} stepData={step.outputStepDatas[0]} />
                ) : null,
            }}
            status={nodeStatus}
            sectionsProps={
              step.inputStepDatas.length > 1 || step.outputStepDatas.length > 1
                ? {
                    sections: [
                      {
                        leftHeader: 'Inputs',
                        sectionRows: step.inputStepDatas.map((stepData) => ({
                          leftHandle: <StepDataHandle step={step} stepData={stepData} />,
                          primaryText: getInputStepDataShape(step.stepType, step.stepVersion, stepData.name)
                            .displayName,
                          secondaryText: '',
                          icon: getIconForStepDataKind(getExpectedStepDataKind(step, stepData)),
                        })),
                      },
                      {
                        leftHeader: 'Outputs',
                        sectionRows: step.outputStepDatas.map((stepData) => ({
                          rightHandle: <StepDataHandle step={step} stepData={stepData} />,
                          primaryText: getOutputStepDataShape(step.stepType, step.stepVersion, stepData.name)
                            .displayName,
                          secondaryText: '',
                          icon: getIconForStepDataKind(getExpectedStepDataKind(step, stepData)),
                        })),
                      },
                    ].filter(({sectionRows}) => sectionRows.length > 1),
                  }
                : undefined
            }
          />
        </div>
      </StepMenu>
    </StepPopover>
  );
}

const styles = StyleSheet.create({
  node: {
    wordBreak: 'break-word', // `Float` applies `break-all`, which breaks in the middle of words
  },
});
